(() => {
  const SELECTOR = '[data-parity-pdp]';
  const DETAILS_SELECTOR = '[data-parity-pdp-details]';

  function getSelectorList(value) {
    return (value || '')
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean);
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function moveExternalBelow(shell) {
    const target = shell.querySelector('[data-parity-pdp-legacy-below]') || shell.querySelector('.parity-pdp__below-main');
    if (!target) return;

    for (const selector of getSelectorList(shell.dataset.parityPdpExternalBelowSelectors)) {
      for (const element of document.querySelectorAll(selector)) {
        if (shell.contains(element) || element.dataset.parityPdpMovedBelow === 'true') {
          continue;
        }

        element.dataset.parityPdpMovedBelow = 'true';
        element.hidden = true;
        target.appendChild(element);
      }
    }
  }

  function activateDetailsTab(root, tabKey) {
    const tabs = Array.from(root.querySelectorAll('[data-parity-details-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-parity-details-panel]'));

    tabs.forEach((tab) => {
      const active = tab.dataset.parityDetailsTab === tabKey;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const active = panel.dataset.parityDetailsPanel === tabKey;
      panel.classList.toggle('is-active', active);

      if (panel.matches('details')) {
        panel.open = active;

        const content = panel.querySelector('.parity-pdp-details__item-content');
        if (content) {
          content.style.maxHeight = active ? 'none' : '0px';
          content.style.opacity = active ? '1' : '0';
        }
      }
    });
  }

  function setupTabs(root) {
    const tabs = Array.from(root.querySelectorAll('[data-parity-details-tab]'));
    if (!tabs.length || root.dataset.parityTabsReady === 'true') return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateDetailsTab(root, tab.dataset.parityDetailsTab));
    });

    root.dataset.parityTabsReady = 'true';
  }

  function getTextRows(range) {
    const rows = [];

    Array.from(range.getClientRects()).forEach((rect) => {
      if (rect.width <= 0 || rect.height <= 0) return;

      const existing = rows.find((row) => Math.abs(row.top - rect.top) < 2);
      if (existing) {
        existing.left = Math.min(existing.left, rect.left);
        existing.right = Math.max(existing.right, rect.right);
        existing.bottom = Math.max(existing.bottom, rect.bottom);
      } else {
        rows.push({
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          left: rect.left,
        });
      }
    });

    return rows.sort((a, b) => a.top - b.top);
  }

  function updateDescriptionPreviewText(preview) {
    const text = preview.querySelector('.parity-pdp__description-preview-text');
    const link = preview.querySelector('[data-parity-description-readmore]');
    if (!text || !link) return;

    const originalText = preview.dataset.parityDescriptionPreviewText || cleanText(text.textContent);
    preview.dataset.parityDescriptionPreviewText = originalText;

    if (!originalText) return;

    text.textContent = originalText;

    if (window.getComputedStyle(preview).display === 'none') return;

    const textNode = text.firstChild;
    if (!textNode) return;

    const characters = Array.from(originalText);
    const linkRect = link.getBoundingClientRect();
    const linkLeft = linkRect.left - 2;
    const range = document.createRange();

    function isValid(length) {
      text.textContent = characters.slice(0, length).join('').trimEnd();
      range.selectNodeContents(text);

      const rows = getTextRows(range);
      if (rows.length > 3) return false;
      if (rows.length < 3) return true;

      return rows[2].right <= linkLeft;
    }

    let low = 0;
    let high = characters.length;
    let best = 0;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);

      if (isValid(middle)) {
        best = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    let previewText = characters.slice(0, best).join('').trimEnd();
    previewText = previewText.replace(/[,:;.\-\s]+$/, '');

    text.textContent = previewText;
    range.detach();
  }

  function setupDescriptionPreview(shell) {
    shell.querySelectorAll('[data-parity-description-readmore]').forEach((button) => {
      if (button.dataset.parityDescriptionReadmoreReady === 'true') return;

      const preview = button.closest('[data-parity-description-preview]');
      if (preview) {
        updateDescriptionPreviewText(preview);
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();

        const details = shell.querySelector(DETAILS_SELECTOR) || document.querySelector(DETAILS_SELECTOR);
        if (!details) return;

        activateDetailsTab(details, 'description');
        details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      button.dataset.parityDescriptionReadmoreReady = 'true';
    });

    shell.querySelectorAll('[data-parity-description-preview]').forEach((preview) => {
      if (preview.dataset.parityDescriptionPreviewReady === 'true') return;

      let resizeTimer = null;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => updateDescriptionPreviewText(preview), 120);
      }, { passive: true });

      preview.dataset.parityDescriptionPreviewReady = 'true';
    });
  }

  function getRecommendationReplacement(html, recommendationId) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');

    return Array.from(documentFragment.querySelectorAll('[data-parity-recommendations]'))
      .find((element) => element.dataset.parityRecommendationsId === recommendationId) || null;
  }

  function recommendationHasProducts(element) {
    return Boolean(element && !element.hidden && element.querySelector('.parity-pdp-recs__item'));
  }

  function getRecommendationBlockById(recommendationId) {
    return Array.from(document.querySelectorAll('[data-parity-recommendations]'))
      .find((element) => element.dataset.parityRecommendationsId === recommendationId) || null;
  }

  function getRecommendationProductIds(element) {
    if (!element) return [];

    return Array.from(element.querySelectorAll('[data-parity-recommendation-product-id]'))
      .map((item) => item.dataset.parityRecommendationProductId)
      .filter(Boolean);
  }

  function waitForRecommendationBlockLoaded(block) {
    if (!block || block.dataset.parityRecommendationsLoaded === 'true' || recommendationHasProducts(block)) {
      return Promise.resolve(block);
    }

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (block.dataset.parityRecommendationsLoaded === 'true' || recommendationHasProducts(block)) {
          observer.disconnect();
          resolve(block);
        }
      });

      observer.observe(block, {
        attributes: true,
        attributeFilter: ['hidden', 'data-parity-recommendations-loaded'],
        childList: true,
        subtree: true,
      });

      window.setTimeout(() => {
        observer.disconnect();
        resolve(block);
      }, 2500);
    });
  }

  function removeCurrentProductRecommendation(replacement, currentProductId) {
    if (!replacement) return;
    if (!currentProductId) return;

    replacement
      .querySelectorAll('[data-parity-recommendation-product-id]')
      .forEach((item) => {
        if (item.dataset.parityRecommendationProductId !== currentProductId) return;
        item.remove();
      });
  }

  async function getFallbackRecommendationRequests(block) {
    const fallbackIntent = block.dataset.parityRecommendationsFallbackIntent;
    const currentIntent = block.dataset.parityRecommendationsIntent;
    const requests = [];

    if (!fallbackIntent || fallbackIntent === currentIntent || !block.dataset.url) {
      return requests;
    }

    const fallbackUrl = new URL(block.dataset.url, window.location.origin);
    fallbackUrl.searchParams.set('intent', fallbackIntent);

    const sourceId = block.dataset.parityRecommendationsFallbackSourceId;
    if (sourceId) {
      const sourceBlock = await waitForRecommendationBlockLoaded(getRecommendationBlockById(sourceId));
      const seedIds = getRecommendationProductIds(sourceBlock)
        .filter((productId) => productId !== block.dataset.parityRecommendationsProductId);

      if (seedIds.length > 0) {
        const startIndex = Math.floor(Math.random() * seedIds.length);
        const orderedSeedIds = seedIds.slice(startIndex).concat(seedIds.slice(0, startIndex));

        orderedSeedIds.forEach((seedProductId) => {
          const seededUrl = new URL(fallbackUrl.toString());
          seededUrl.searchParams.set('product_id', seedProductId);
          requests.push({
            url: seededUrl.toString(),
          });
        });

        return requests;
      }
    }

    requests.push({
      url: fallbackUrl.toString(),
    });

    return requests;
  }

  function setupRecommendationControls(block) {
    if (!block || block.dataset.parityRecommendationsControlsReady === 'true') return;

    const scroller = block.querySelector('[data-parity-recommendations-scroller]');
    if (!scroller) return;

    const previousButtons = Array.from(block.querySelectorAll('[data-parity-recommendations-prev]'));
    const nextButtons = Array.from(block.querySelectorAll('[data-parity-recommendations-next]'));

    const isVertical = () => window.matchMedia('(min-width: 1200px)').matches
      && block.classList.contains('parity-pdp-recs--aside');

    const getMaxScroll = () => {
      if (isVertical()) {
        return Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      }

      return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    };

    const updateButtons = () => {
      const vertical = isVertical();
      const position = vertical ? scroller.scrollTop : scroller.scrollLeft;
      const max = getMaxScroll();
      const atStart = position <= 2;
      const atEnd = max <= 2 || position >= max - 2;

      previousButtons.forEach((previousButton) => {
        previousButton.disabled = atStart;
        previousButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      });

      nextButtons.forEach((nextButton) => {
        nextButton.disabled = atEnd;
        nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      });
    };

    const scroll = (direction) => {
      const vertical = isVertical();
      const item = block.querySelector('.parity-pdp-recs__item');
      const amount = vertical
        ? getMaxScroll()
        : Math.max(scroller.clientWidth * 0.78, item?.getBoundingClientRect().width || 180);

      if (amount <= 0) return;

      scroller[vertical ? 'scrollTo' : 'scrollBy']({
        top: vertical ? (direction > 0 ? amount : 0) : 0,
        left: vertical ? 0 : amount * direction,
        behavior: 'smooth',
      });
    };

    previousButtons.forEach((previousButton) => {
      previousButton.addEventListener('click', () => scroll(-1));
    });

    nextButtons.forEach((nextButton) => {
      nextButton.addEventListener('click', () => scroll(1));
    });

    scroller.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });
    requestAnimationFrame(updateButtons);

    block.dataset.parityRecommendationsControlsReady = 'true';
  }

  async function loadRecommendationBlock(block, url, isFallback = false) {
    if (!block || block.dataset.parityRecommendationsLoading === 'true') return;

    block.dataset.parityRecommendationsLoading = 'true';

    try {
      const response = await window.fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

      const replacement = getRecommendationReplacement(await response.text(), block.dataset.parityRecommendationsId);

      if (isFallback) {
        removeCurrentProductRecommendation(replacement, block.dataset.parityRecommendationsProductId);
      }

      if (recommendationHasProducts(replacement)) {
        block.className = replacement.className;
        block.innerHTML = replacement.innerHTML;
        block.hidden = false;
        block.dataset.parityRecommendationsLoaded = 'true';
        setupRecommendationControls(block);
        return true;
      }

      if (!isFallback) {
        const fallbackRequests = await getFallbackRecommendationRequests(block);

        for (const fallbackRequest of fallbackRequests) {
          block.dataset.parityRecommendationsLoading = 'false';
          const loaded = await loadRecommendationBlock(
            block,
            fallbackRequest.url,
            true,
          );

          if (loaded) return true;
        }
      }

      block.hidden = true;
      block.dataset.parityRecommendationsLoaded = 'true';
      return false;
    } catch (_error) {
      block.hidden = true;
      return false;
    } finally {
      block.dataset.parityRecommendationsLoading = 'false';
    }
  }

  function setupRecommendations(root = document) {
    root.querySelectorAll('[data-parity-recommendations]').forEach((block) => {
      setupRecommendationControls(block);

      if (block.dataset.parityRecommendationsLoaded === 'true' || recommendationHasProducts(block)) {
        block.dataset.parityRecommendationsLoaded = 'true';
        return;
      }

      if (!block.dataset.url) return;
      loadRecommendationBlock(block, block.dataset.url);
    });
  }

  function setupReadMore(root) {
    root.querySelectorAll('[data-parity-readmore-scope]').forEach((scope) => {
      const content = scope.querySelector('[data-parity-readmore]');
      const toggle = scope.querySelector('[data-parity-readmore-toggle]');
      if (!content || !toggle || toggle.dataset.parityReadmoreReady === 'true') return;

      function updateToggle() {
        const collapsed = content.dataset.parityReadmoreCollapsed !== 'false';
        const clipped = content.scrollHeight > content.clientHeight + 8;
        toggle.hidden = collapsed ? !clipped : false;
      }

      content.dataset.parityReadmoreCollapsed = 'true';
      toggle.addEventListener('click', () => {
        const startHeight = content.clientHeight;
        content.style.maxHeight = `${startHeight}px`;
        content.dataset.parityReadmoreCollapsed = 'false';
        content.dataset.parityReadmoreExpanded = 'true';

        requestAnimationFrame(() => {
          content.style.maxHeight = `${content.scrollHeight}px`;
        });

        content.addEventListener('transitionend', () => {
          content.style.maxHeight = '';
          toggle.hidden = true;
          toggle.remove();
        }, { once: true });
      });

      toggle.dataset.parityReadmoreReady = 'true';
      requestAnimationFrame(updateToggle);
      window.addEventListener('resize', updateToggle, { passive: true });
    });
  }

  function setupAccordions(root) {
    root.querySelectorAll('.parity-pdp-details__item').forEach((item) => {
      const summary = item.querySelector('.parity-pdp-details__summary');
      const content = item.querySelector('.parity-pdp-details__item-content');
      if (!summary || !content || item.dataset.parityAccordionReady === 'true') return;

      content.style.maxHeight = item.open ? 'none' : '0px';
      content.style.opacity = item.open ? '1' : '0';

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (item.dataset.parityAccordionAnimating === 'true') return;

        const opening = !item.open;
        item.dataset.parityAccordionAnimating = 'true';

        if (opening) {
          item.open = true;
          content.style.maxHeight = '0px';
          content.style.opacity = '0';

          requestAnimationFrame(() => {
            content.style.maxHeight = `${content.scrollHeight}px`;
            content.style.opacity = '1';
          });
        } else {
          content.style.maxHeight = `${content.scrollHeight}px`;
          content.style.opacity = '1';

          requestAnimationFrame(() => {
            content.style.maxHeight = '0px';
            content.style.opacity = '0';
          });
        }

        content.addEventListener('transitionend', () => {
          if (opening) {
            content.style.maxHeight = 'none';
            content.style.opacity = '1';
          } else {
            item.open = false;
          }

          item.dataset.parityAccordionAnimating = 'false';
        }, { once: true });
      });

      item.dataset.parityAccordionReady = 'true';
    });
  }

  function parsePartNumberVariants(root) {
    if (root.parityPartNumberVariants) {
      return root.parityPartNumberVariants;
    }

    const source = root.querySelector('[data-parity-part-numbers-source]');
    if (!source) {
      root.parityPartNumberVariants = [];
      return root.parityPartNumberVariants;
    }

    try {
      const variants = JSON.parse(source.textContent || '[]');
      root.parityPartNumberVariants = Array.isArray(variants) ? variants : [];
    } catch (_error) {
      root.parityPartNumberVariants = [];
    }

    return root.parityPartNumberVariants;
  }

  function getVariantData(root, variantId = getCurrentVariantId(root)) {
    const variants = parsePartNumberVariants(root);
    if (!variants.length) return null;

    return variants.find((item) => String(item.id) === String(variantId)) || variants[0] || null;
  }

  function getVariantIdFromEvent(event) {
    const detail = event?.detail || {};
    const variant = detail.variant || detail.currentVariant || detail.selectedVariant;

    return variant?.id || detail.variantId || detail.id || null;
  }

  function getCurrentVariantId(root) {
    const shell = root.closest(SELECTOR) || document;

    const variantInputSelectors = [
      'form.js-product-form-main [name="id"]',
      'form[data-product-form] [name="id"]',
      '[data-product-form] [name="id"]',
    ];

    for (const selector of variantInputSelectors) {
      const scopedInput = shell.querySelector(selector);
      if (scopedInput?.value && scopedInput.value !== 'not-selected') {
        return scopedInput.value;
      }
    }

    const selectedOption = shell.querySelector('select[name="id"] option:checked');
    if (selectedOption?.value && selectedOption.value !== 'not-selected') {
      return selectedOption.value;
    }

    return new URLSearchParams(window.location.search).get('variant');
  }

  function getVariantField(root) {
    const shell = root.closest(SELECTOR) || document;
    const selectors = [
      'form.js-product-form-main [name="id"]',
      'form[data-product-form] [name="id"]',
      '[data-product-form] [name="id"]',
    ];

    for (const selector of selectors) {
      const field = shell.querySelector(selector);
      if (field) return field;
    }

    return null;
  }

  function getVariantSelectionElement(root, trigger) {
    const shell = root.closest(SELECTOR) || document;
    const optionsSelection = trigger?.closest?.('options-selection');
    const variantSelectionId = optionsSelection?.getAttribute?.('variant-selection');

    if (variantSelectionId) {
      const scopedSelection = shell.querySelector(`#${CSS.escape(variantSelectionId)}`);
      if (scopedSelection) return scopedSelection;

      return document.getElementById(variantSelectionId);
    }

    return shell.querySelector('variant-selection[data-variant-selection], variant-selection');
  }

  function getSelectedVisibleOptionValues(root, trigger) {
    const shell = root.closest(SELECTOR) || document;
    const optionScope = trigger?.closest?.('options-selection, [data-options-selection]')
      || shell.querySelector('options-selection, [data-options-selection]');
    const scope = optionScope || shell;
    const values = [];

    scope.querySelectorAll('[data-variant-option]').forEach((optionNode) => {
      const index = Number(optionNode.dataset.variantOptionIndex);
      if (Number.isNaN(index)) return;

      let value = '';
      if (optionNode.matches('select')) {
        value = optionNode.value;
      } else {
        const select = optionNode.querySelector('select');
        const checked = optionNode.querySelector('[data-variant-option-value]:checked, input:checked');
        value = checked?.value || select?.value || '';
      }

      if (value && value !== 'not-selected' && value !== 'false') {
        values[index] = value;
      }
    });

    scope.querySelectorAll('input[data-variant-option-value]:checked').forEach((input) => {
      const index = Number(input.dataset.variantOptionValueIndex);
      if (Number.isNaN(index)) return;

      const value = input.value;
      if (value && value !== 'not-selected' && value !== 'false') {
        values[index] = value;
      }
    });

    return values;
  }

  function findVariantByOptions(root, optionValues) {
    const selectedValues = optionValues.filter((value) => value && value !== 'not-selected' && value !== 'false');
    if (!selectedValues.length) return null;

    return parsePartNumberVariants(root).find((variant) => {
      if (Array.isArray(variant.options) && variant.options.length) {
        return optionValues.every((value, index) => {
          if (!value || value === 'not-selected' || value === 'false') return true;

          return String(variant.options[index] || '') === String(value);
        });
      }

      return selectedValues.length === 1 && String(variant.style || '') === String(selectedValues[0]);
    }) || null;
  }

  function updateVisibleOptionState(root, trigger, optionValues) {
    const shell = root.closest(SELECTOR) || document;
    const optionScope = trigger?.closest?.('options-selection, [data-options-selection]')
      || shell.querySelector('options-selection, [data-options-selection]');
    const scope = optionScope || shell;

    scope.querySelectorAll('[data-variant-option]').forEach((optionNode) => {
      const index = Number(optionNode.dataset.variantOptionIndex);
      const value = Number.isNaN(index) ? '' : optionValues[index];
      if (!value || value === 'not-selected' || value === 'false') return;

      optionNode.dataset.variantOptionChosenValue = value;

      const header = optionNode.closest('fieldset, .options-selection__select-wrapper')
        ?.querySelector?.('[data-variant-option-name]');
      if (header) {
        const currentLabel = cleanText(header.textContent);
        const labelPrefix = currentLabel.includes(':')
          ? currentLabel.split(':')[0].trim()
          : cleanText(header.dataset.variantOptionChooseName).replace(/^Choose a\s+/i, '').trim() || 'Option';

        header.textContent = `${labelPrefix}: ${value}`;
      }

      optionNode.querySelectorAll('[data-variant-option-value-wrapper]').forEach((wrapper) => {
        const control = wrapper.matches('[data-variant-option-value]')
          ? wrapper
          : wrapper.querySelector('[data-variant-option-value]');
        const isSelected = String(control?.value || '') === String(value);

        wrapper.classList.toggle('options-selection__option-value--selected', isSelected);
        if (control?.matches?.('input[type="radio"], input[type="checkbox"]')) {
          control.checked = isSelected;
        }
        if (control?.matches?.('option')) {
          control.selected = isSelected;
        }
      });

      if (optionNode.matches('select')) {
        optionNode.value = value;
      }
    });
  }

  function setVariantFieldValue(field, variantId) {
    if (!field) return false;

    const nextValue = String(variantId);
    const currentValue = field.value ? String(field.value) : '';
    if (currentValue === nextValue) return true;

    field.value = nextValue;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));

    return true;
  }

  function dispatchVariantSelectionEvents(root, variant) {
    if (!variant) return;

    const shell = root.closest(SELECTOR) || document;
    const detail = {
      variant,
      currentVariant: variant,
      selectedVariant: variant,
      variantId: variant.id,
    };

    ['variant:change', 'variant-change', 'on:variant:change'].forEach((eventName) => {
      shell.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
    });
  }

  function syncVariantFromVisibleOptions(root, trigger) {
    if (!root || root.dataset.parityVariantSyncing === 'true') return null;

    const optionValues = getSelectedVisibleOptionValues(root, trigger);
    const variant = findVariantByOptions(root, optionValues);
    if (!variant) return null;

    root.dataset.parityVariantSyncing = 'true';
    try {
      const field = getVariantField(root);
      setVariantFieldValue(field, variant.id);
      updateVisibleOptionState(root, trigger, optionValues);

      const variantSelection = getVariantSelectionElement(root, trigger);
      if (variantSelection) {
        variantSelection.setAttribute('variant', String(variant.id));
      }
    } finally {
      delete root.dataset.parityVariantSyncing;
    }

    updateSelectedVariantState(root, variant.id);
    dispatchVariantSelectionEvents(root, variant);

    return variant;
  }

  function textFromHtml(html) {
    const element = document.createElement('div');
    element.innerHTML = html || '';

    return cleanText(element.textContent);
  }

  function stripDescriptionPreviewPrefix(value) {
    return cleanText(value).replace(/^features:?\s*/i, '').trim();
  }

  function updateDescriptionPreviews(root, html) {
    const shell = root.closest(SELECTOR) || document;
    const previewText = stripDescriptionPreviewPrefix(textFromHtml(html));
    if (!previewText) return;

    shell.querySelectorAll('[data-parity-description-preview]').forEach((preview) => {
      const text = preview.querySelector('.parity-pdp__description-preview-text');
      if (!text) return;

      preview.dataset.parityDescriptionPreviewText = previewText;
      text.textContent = previewText;
      updateDescriptionPreviewText(preview);
    });
  }

  function resetReadMore(root) {
    root.querySelectorAll('[data-parity-readmore-scope]').forEach((scope) => {
      const content = scope.querySelector('[data-parity-readmore]');
      const toggle = scope.querySelector('[data-parity-readmore-toggle]');
      if (!content) return;

      content.dataset.parityReadmoreCollapsed = 'true';
      delete content.dataset.parityReadmoreExpanded;
      content.style.maxHeight = '';
      content.style.opacity = '';

      if (toggle?.isConnected) {
        toggle.hidden = true;
        delete toggle.dataset.parityReadmoreReady;
      }
    });

    setupReadMore(root);
  }

  function updateVariantDescription(root, variant) {
    if (root.dataset.parityHasMultipleVariants !== 'true' || !variant) return;

    const variantTemplate = root.querySelector(`[data-parity-variant-description-template][data-variant-id="${variant.id}"]`);
    const productTemplate = root.querySelector('[data-parity-product-description-template]');
    const template = variantTemplate || productTemplate;
    if (!template) return;

    const descriptionKey = `${variant.id}:${variantTemplate ? 'variant' : 'product'}`;
    if (root.dataset.parityCurrentDescriptionKey === descriptionKey) return;

    const html = template.innerHTML;
    root.querySelectorAll('[data-parity-variant-description-target]').forEach((target) => {
      target.innerHTML = html;
    });

    root.dataset.parityCurrentDescriptionKey = descriptionKey;
    resetReadMore(root);
    updateDescriptionPreviews(root, html);
  }

  function getVariantMediaIds(variant) {
    return [
      variant?.featuredMediaId,
      variant?.featuredImageId,
    ]
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map((id) => String(id));
  }

  function getImageSignature(value) {
    const src = String(value || '').split(/[?#]/)[0];
    if (!src) return '';

    return decodeURIComponent(src.substring(src.lastIndexOf('/') + 1));
  }

  function findMediaById(container, mediaIds, selector = '[data-media-id]') {
    if (!container || !mediaIds.length) return null;

    return Array.from(container.querySelectorAll(selector))
      .find((element) => mediaIds.includes(String(element.dataset.mediaId))) || null;
  }

  function findMediaByImage(container, imageSrc) {
    const signature = getImageSignature(imageSrc);
    if (!container || !signature) return null;

    const image = Array.from(container.querySelectorAll('img[src], img[srcset]')).find((img) => {
      const srcMatch = getImageSignature(img.currentSrc || img.src) === signature;
      const srcsetMatch = String(img.getAttribute('srcset') || '').includes(signature);

      return srcMatch || srcsetMatch;
    });

    return image?.closest?.('[data-media-id], button, [data-swiper-slide], .swiper-slide') || null;
  }

  function isMediaActive(element) {
    return Boolean(element?.matches?.(
      '.media-gallery__slide--active, .is-current-variant, .swiper-slide-active, .swiper-slide-thumb-active'
    ));
  }

  function selectVariantMedia(root, variant) {
    if (!variant) return;

    const shell = root.closest(SELECTOR) || document;
    const mediaIds = getVariantMediaIds(variant);
    const mediaKey = mediaIds.join('|') || getImageSignature(variant.featuredImageSrc);
    if (!mediaKey || root.dataset.paritySelectedMediaKey === mediaKey) return;

    const productGallery = shell.querySelector('product-gallery');
    if (productGallery?.showVariantMedia && variant.featuredMediaPosition) {
      productGallery.showVariantMedia({
        featured_media: {
          id: variant.featuredMediaId,
          position: variant.featuredMediaPosition,
        },
      });
      root.dataset.paritySelectedMediaKey = mediaKey;
      return;
    }

    const gallery = shell.querySelector('media-gallery, [data-media-gallery]');
    if (gallery) {
      if (typeof gallery.goToSlide === 'function') {
        const slides = Array.from(gallery.slides || gallery.querySelectorAll('.media-gallery__slide'));
        const targetSlide = slides.find((slide) => mediaIds.includes(String(slide.dataset.mediaId)))
          || findMediaByImage(gallery, variant.featuredImageSrc)?.closest?.('.media-gallery__slide');
        const targetIndex = slides.indexOf(targetSlide);

        if (targetSlide && isMediaActive(targetSlide)) {
          root.dataset.paritySelectedMediaKey = mediaKey;
          return;
        }

        if (targetIndex >= 0) {
          const direction = targetIndex > (gallery.currentIndex || 0) ? 'next' : 'prev';
          gallery.goToSlide(targetIndex, direction);
          root.dataset.paritySelectedMediaKey = mediaKey;
          return;
        }
      }

      if (typeof gallery.customSetActiveMedia === 'function') {
        const targetMedia = findMediaById(gallery, mediaIds, '.media-viewer__item[data-media-id], [data-media-id]')
          || findMediaByImage(gallery, variant.featuredImageSrc)?.closest?.('.media-viewer__item, [data-media-id]');

        if (targetMedia) {
          gallery.customSetActiveMedia(targetMedia, true);
          root.dataset.paritySelectedMediaKey = mediaKey;
          return;
        }
      }
    }

    const thumbnail = findMediaById(shell, mediaIds, 'button[data-media-id], [data-swiper-thumbnail][data-media-id], [data-media-id]')
      || findMediaByImage(shell, variant.featuredImageSrc);

    if (thumbnail && !isMediaActive(thumbnail)) {
      thumbnail.click();
      root.dataset.paritySelectedMediaKey = mediaKey;
    }
  }

  function updatePartNumbers(root, variantId = getCurrentVariantId(root)) {
    const variant = getVariantData(root, variantId);
    if (!variant) return null;

    const hasMpn = Boolean(cleanText(variant.mpn));

    root.querySelectorAll('[data-parity-part-number-heading="mpn"]').forEach((headingNode) => {
      headingNode.hidden = !hasMpn;
    });

    root.querySelectorAll('[data-parity-part-number-field]').forEach((fieldNode) => {
      const key = fieldNode.dataset.parityPartNumberField;
      const hideField = key === 'mpn' && !hasMpn;
      const value = variant[key] || (key === 'storeSku' ? '-' : '');

      fieldNode.hidden = hideField;
      if (hideField) {
        fieldNode.textContent = '';
        return;
      }

      fieldNode.textContent = value;
    });

    return variant;
  }

  function updateIdentifierValue(group, key, value) {
    const wrapper = group.querySelector(`[data-parity-identifier="${key}"]`);
    const valueNode = group.querySelector(`[data-parity-identifier-value="${key}"]`);
    if (!wrapper) return false;

    const text = cleanText(value);
    wrapper.hidden = !text;

    if (valueNode) {
      valueNode.textContent = text;
    }

    return Boolean(text);
  }

  function updateIdentifiers(root, variant) {
    if (!variant) return;

    const shell = root.closest(SELECTOR) || document;
    shell.querySelectorAll('[data-parity-identifiers]').forEach((group) => {
      const hasMpn = updateIdentifierValue(group, 'mpn', variant.mpn);
      const hasSku = updateIdentifierValue(group, 'sku', variant.identifierSku);
      const hasReviewSummary = Boolean(group.querySelector('[data-parity-reviews-trigger]'));
      const separator = group.querySelector('[data-parity-identifier-separator]');

      if (separator) {
        separator.hidden = !(hasMpn && hasSku);
      }

      group.hidden = !(hasMpn || hasSku || hasReviewSummary);
    });
  }

  function updateSelectedVariantState(root, variantId = getCurrentVariantId(root)) {
    const variant = updatePartNumbers(root, variantId);
    if (!variant) return;

    updateIdentifiers(root, variant);
    updateVariantDescription(root, variant);
    selectVariantMedia(root, variant);
  }

  function schedulePartNumberUpdate(root, variantId) {
    window.setTimeout(() => updateSelectedVariantState(root, variantId), 0);
    window.setTimeout(() => updateSelectedVariantState(root, variantId || getCurrentVariantId(root)), 120);
    window.setTimeout(() => updateSelectedVariantState(root, getCurrentVariantId(root)), 450);
  }

  function setupPartNumbers(root) {
    updateSelectedVariantState(root);

    if (root.dataset.parityPartNumbersReady === 'true') return;

    const onVariantChange = (event) => {
      schedulePartNumberUpdate(root, getVariantIdFromEvent(event));
    };

    document.addEventListener('on:variant:change', onVariantChange, true);
    document.addEventListener('variant:change', onVariantChange, true);
    document.addEventListener('variant-change', onVariantChange, true);

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target?.matches?.('input[name="id"], select[name="id"], [data-variant-option-value], [data-variant-option], [data-variant-option] input, [data-variants]')) return;
      if (!(root.closest(SELECTOR) || document).contains(target)) return;

      if (target.matches('[data-variant-option-value], [data-variant-option], [data-variant-option] input')) {
        syncVariantFromVisibleOptions(root, target);
      }

      schedulePartNumberUpdate(root);
    }, true);

    document.addEventListener('click', (event) => {
      const control = event.target?.closest?.('[data-variant-option-value], [data-variant-option], .options-selection__option-value, .variant-picker__option, .js-option');
      if (!control || !(root.closest(SELECTOR) || document).contains(control)) return;

      window.setTimeout(() => syncVariantFromVisibleOptions(root, control), 0);
      window.setTimeout(() => syncVariantFromVisibleOptions(root, control), 120);
      schedulePartNumberUpdate(root);
    }, true);

    const observer = new MutationObserver(() => schedulePartNumberUpdate(root));
    (root.closest(SELECTOR) || document).querySelectorAll('input[name="id"], select[name="id"], [data-variants]').forEach((input) => {
      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value', 'selected', 'variant'],
        childList: true,
        subtree: true,
      });
    });

    root.dataset.parityPartNumbersReady = 'true';
  }

  function expandYears(value) {
    const years = [];
    const matches = String(value || '').match(/(?:19|20)\d{2}(?:\s*[-–—]\s*(?:19|20)\d{2})?/g) || [];

    matches.forEach((match) => {
      const nums = (match.match(/(?:19|20)\d{2}/g) || []).map(Number);
      if (!nums.length) return;

      if (nums.length === 1) {
        years.push(nums[0]);
        return;
      }

      const start = Math.min(nums[0], nums[1]);
      const end = Math.max(nums[0], nums[1]);
      for (let year = start; year <= end; year += 1) {
        years.push(year);
      }
    });

    return years;
  }

  function formatYearRun(run) {
    if (run.length > 5) {
      return `${run[0]}-${run[run.length - 1]}`;
    }

    return run.join(', ');
  }

  function formatYears(years) {
    const normalizedYears = Array.from(new Set(years.map(Number).filter(Boolean)))
      .sort((a, b) => a - b);
    const runs = [];

    normalizedYears.forEach((year) => {
      const lastRun = runs[runs.length - 1];
      const previousYear = lastRun?.[lastRun.length - 1];

      if (lastRun && year === previousYear + 1) {
        lastRun.push(year);
        return;
      }

      runs.push([year]);
    });

    return runs.map(formatYearRun).join(', ');
  }

  function parseFitmentText(text) {
    const makeMap = new Map();

    String(text || '').split(/\r?\n/).forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) return;

      const parts = line.split('|').map((part) => part.trim()).filter(Boolean);
      if (parts.length < 3) return;

      const years = expandYears(parts[0]);
      const make = cleanText(parts[1]);
      const model = cleanText(parts.slice(2).join(' '));
      if (!years.length || !make || !model) return;

      if (!makeMap.has(make)) {
        makeMap.set(make, new Map());
      }

      const modelMap = makeMap.get(make);
      const existing = modelMap.get(model) || [];
      modelMap.set(model, existing.concat(years));
    });

    return makeMap;
  }

  function renderFitmentGroups(container, makeMap) {
    container.replaceChildren();

    if (!makeMap.size) {
      const empty = document.createElement('p');
      empty.className = 'parity-pdp-details__fitment-empty';
      empty.textContent = 'Fitment data is not available for this product yet.';
      container.appendChild(empty);
      return;
    }

    makeMap.forEach((modelMap, make) => {
      const group = document.createElement('section');
      group.className = 'parity-pdp-details__fitment-group';

      const heading = document.createElement('h4');
      heading.className = 'parity-pdp-details__fitment-make';
      heading.textContent = make;
      group.appendChild(heading);

      const list = document.createElement('div');
      list.className = 'parity-pdp-details__fitment-list';

      Array.from(modelMap.entries())
        .sort(([modelA], [modelB]) => modelA.localeCompare(modelB))
        .forEach(([model, years]) => {
          const item = document.createElement('p');
          const modelText = document.createElement('span');
          const yearText = document.createElement('span');

          item.className = 'parity-pdp-details__fitment-item';
          modelText.className = 'parity-pdp-details__fitment-model';
          yearText.className = 'parity-pdp-details__fitment-years';
          modelText.textContent = model;
          yearText.textContent = formatYears(years);

          item.append(modelText, ' ', yearText);
          list.appendChild(item);
        });

      group.appendChild(list);
      container.appendChild(group);
    });
  }

  function getSourceFitmentWidget() {
    return Array.from(document.querySelectorAll('.cm_vehicle-widget__verify-fitment'))
      .find((widget) => !widget.closest('[data-parity-fitment-content]')) || null;
  }

  function getSourceFitmentWidgetFromNode(node) {
    const widget = node?.closest?.('.cm_vehicle-widget__verify-fitment');
    if (!widget || widget.closest('[data-parity-fitment-content]')) return null;
    return widget;
  }

  function getSourceFitmentResultNode() {
    return getSourceFitmentWidget()?.querySelector('.cm_vehicle-widget_label.cm_verify-fitment') || null;
  }

  function getSourceFitmentControl(name) {
    const widget = getSourceFitmentWidget();
    if (!widget) return null;

    if (name === 'go') {
      return widget.querySelector('.cm_vehicle-widget_button__go, [role="button"].button-primary');
    }

    if (name === 'clear') {
      return widget.querySelector('.cm_vehicle-widget_button__clear, [role="button"].button-secondary');
    }

    const label = name.charAt(0).toUpperCase() + name.slice(1);
    return widget.querySelector(`select[aria-label="${label}"]`);
  }

  function getSourceFitmentChangeControl() {
    const widget = getSourceFitmentWidget();
    if (!widget) return null;

    return widget.querySelector('.cm_verify-fitment_change, [data-cm-role="change"]');
  }

  function isControlDisabled(control) {
    if (!control) return true;
    return control.disabled || control.getAttribute('disabled') === 'true';
  }

  function getSelectOptionSignature(select) {
    return Array.from(select?.options || [])
      .map((option) => `${option.value}\u0000${option.textContent.trim()}`)
      .join('\u0001');
  }

  function setSelectPlaceholder(target) {
    const label = target.getAttribute('aria-label') || 'Select';
    const signature = `\u0000${label}`;

    if (target.dataset.parityFitmentOptionSignature !== signature) {
      target.replaceChildren(new Option(label, ''));
      target.dataset.parityFitmentOptionSignature = signature;
    }

    if (target.value !== '') target.value = '';
    target.disabled = true;
  }

  function sortMakeSelectOptions(select) {
    window.ParityConvermaxMakeOrder?.sortSelect(select);
  }

  function copySelectOptions(source, target) {
    if (!target) return;

    if (!source) {
      setSelectPlaceholder(target);
      return;
    }

    if (target.dataset.parityFitmentProxy === 'make') {
      sortMakeSelectOptions(source);
    }

    const sourceSignature = getSelectOptionSignature(source);

    if (target.dataset.parityFitmentOptionSignature !== sourceSignature) {
      const fragment = document.createDocumentFragment();

      Array.from(source.options).forEach((sourceOption) => {
        fragment.appendChild(sourceOption.cloneNode(true));
      });

      target.replaceChildren(fragment);
      target.dataset.parityFitmentOptionSignature = sourceSignature;

      if (target.dataset.parityFitmentProxy === 'make') {
        sortMakeSelectOptions(target);
      }
    }

    if (target.value !== source.value) {
      target.value = source.value;
    }
    target.disabled = source.disabled;
  }

  function clickConvermaxControl(control) {
    if (!control || isControlDisabled(control)) return;
    if (typeof control.click === 'function') {
      control.click();
      return;
    }

    control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  function getFitmentProxyParts(selector) {
    return {
      result: selector.querySelector('[data-parity-fitment-result]'),
      fields: selector.querySelector('.parity-pdp-details__fitment-fields'),
      actions: selector.querySelector('.parity-pdp-details__fitment-actions'),
      year: selector.querySelector('[data-parity-fitment-proxy="year"]'),
      make: selector.querySelector('[data-parity-fitment-proxy="make"]'),
      model: selector.querySelector('[data-parity-fitment-proxy="model"]'),
      go: selector.querySelector('[data-parity-fitment-proxy-go]'),
      clear: selector.querySelector('[data-parity-fitment-proxy-clear]'),
    };
  }

  function mirrorSourceFitmentResult(selector, root, sourceResult) {
    const { result, fields, actions } = getFitmentProxyParts(selector);
    if (!result) return;

    result.replaceChildren();

    if (!sourceResult) {
      result.hidden = true;
      if (fields) fields.hidden = false;
      if (actions) actions.hidden = false;
      selector.dataset.parityFitmentState = 'select';
      return;
    }

    const clone = sourceResult.cloneNode(true);
    clone.classList.add('parity-pdp-details__fitment-result-card');
    result.appendChild(clone);
    result.hidden = false;
    if (fields) fields.hidden = true;
    if (actions) actions.hidden = true;
    selector.dataset.parityFitmentState = sourceResult.classList.contains('cm_verify-fitment__no') ? 'no' : 'yes';

    clone.querySelectorAll('.cm_verify-fitment_change').forEach((change) => {
      change.addEventListener('click', (event) => {
        event.preventDefault();
        clickConvermaxControl(getSourceFitmentChangeControl());
        scheduleFitmentProxySync(root, 80);
      });
    });
  }

  function resetFitmentProxySelection(root) {
    root.querySelectorAll('[data-parity-fitment-selector]').forEach((selector) => {
      const { result, fields, actions, year, make, model, go, clear } = getFitmentProxyParts(selector);

      if (result) {
        result.replaceChildren();
        result.hidden = true;
      }

      if (fields) fields.hidden = false;
      if (actions) actions.hidden = false;
      selector.dataset.parityFitmentState = 'select';

      [year, make, model].forEach((select) => {
        if (!select) return;
        select.value = '';
      });

      if (year) year.disabled = false;
      if (make) make.disabled = true;
      if (model) model.disabled = true;
      if (go) go.disabled = true;
      if (clear) clear.disabled = true;
    });
  }

  function isSourceFitmentCleared() {
    const sourceResult = getSourceFitmentResultNode();
    if (sourceResult) return false;

    return ['year', 'make', 'model'].every((name) => {
      const control = getSourceFitmentControl(name);
      return !control || control.value === '';
    });
  }

  function beginFitmentProxyClear(root) {
    root.dataset.parityFitmentClearing = 'true';
    resetFitmentProxySelection(root);

    window.setTimeout(() => {
      if (root.dataset.parityFitmentClearing !== 'true') return;
      delete root.dataset.parityFitmentClearing;
      syncFitmentProxyControls(root);
    }, 3500);
  }

  function syncFitmentProxyControls(root) {
    if (root.dataset.parityFitmentClearing === 'true') {
      if (!isSourceFitmentCleared()) return;
      delete root.dataset.parityFitmentClearing;
    }

    const sourceResult = getSourceFitmentResultNode();

    root.querySelectorAll('[data-parity-fitment-selector]').forEach((selector) => {
      const { year, make, model, go, clear } = getFitmentProxyParts(selector);

      mirrorSourceFitmentResult(selector, root, sourceResult);
      if (sourceResult) return;

      copySelectOptions(getSourceFitmentControl('year'), year);
      copySelectOptions(getSourceFitmentControl('make'), make);
      copySelectOptions(getSourceFitmentControl('model'), model);

      if (go) go.disabled = isControlDisabled(getSourceFitmentControl('go'));
      if (clear) clear.disabled = isControlDisabled(getSourceFitmentControl('clear')) && !getSourceFitmentChangeControl();
    });
  }

  function scheduleFitmentProxySync(root, delay = 450) {
    window.setTimeout(() => syncFitmentProxyControls(root), 0);
    window.setTimeout(() => syncFitmentProxyControls(root), delay);
    window.setTimeout(() => syncFitmentProxyControls(root), delay + 250);
    window.setTimeout(() => syncFitmentProxyControls(root), delay + 900);
  }

  function applySourceFitmentValue(name, value, root) {
    const source = getSourceFitmentControl(name);
    if (!source) return false;

    source.value = value;
    source.dispatchEvent(new Event('input', { bubbles: true }));
    source.dispatchEvent(new Event('change', { bubbles: true }));
    scheduleFitmentProxySync(root);
    return true;
  }

  function setSourceFitmentValue(name, value, root) {
    delete root.dataset.parityFitmentClearing;

    if (applySourceFitmentValue(name, value, root)) return;

    const change = getSourceFitmentChangeControl();
    if (!change) return;

    clickConvermaxControl(change);
    scheduleFitmentProxySync(root, 150);

    window.setTimeout(() => {
      applySourceFitmentValue(name, value, root);
    }, 350);

    window.setTimeout(() => {
      applySourceFitmentValue(name, value, root);
      syncFitmentProxyControls(root);
    }, 900);
  }

  function setupFitmentSelectorProxy(root) {
    root.querySelectorAll('[data-parity-fitment-selector]').forEach((selector) => {
      if (selector.dataset.parityFitmentSelectorReady === 'true') return;

      selector.querySelectorAll('[data-parity-fitment-proxy]').forEach((select) => {
        select.addEventListener('change', () => {
          setSourceFitmentValue(select.dataset.parityFitmentProxy, select.value, root);
        });
      });

      const go = selector.querySelector('[data-parity-fitment-proxy-go]');
      if (go) {
        go.addEventListener('click', () => {
          clickConvermaxControl(getSourceFitmentControl('go'));
          scheduleFitmentProxySync(root);
        });
      }

      const clear = selector.querySelector('[data-parity-fitment-proxy-clear]');
      if (clear) {
        clear.addEventListener('click', () => {
          beginFitmentProxyClear(root);

          const clearControl = getSourceFitmentControl('clear');
          if (clearControl) {
            clickConvermaxControl(clearControl);
          } else {
            clickConvermaxControl(getSourceFitmentChangeControl());
          }

          scheduleFitmentProxySync(root, 150);
        });
      }

      selector.dataset.parityFitmentSelectorReady = 'true';
    });

    setupSourceFitmentObserver(root);
    setupSourceFitmentDelegates(root);
    syncFitmentProxyControls(root);

    if (root.dataset.parityFitmentGarageEventReady !== 'true') {
      window.addEventListener('parity:garage-vehicle-selected', () => {
        scheduleFitmentProxySync(root, 150);
      });
      root.dataset.parityFitmentGarageEventReady = 'true';
    }

    if (root.dataset.parityFitmentProxyRetryReady !== 'true') {
      let attempts = 0;
      const retry = window.setInterval(() => {
        attempts += 1;
        setupSourceFitmentObserver(root);
        setupSourceFitmentDelegates(root);
        syncFitmentProxyControls(root);
        if (getSourceFitmentWidget() || attempts >= 20) {
          window.clearInterval(retry);
        }
      }, 500);

      root.dataset.parityFitmentProxyRetryReady = 'true';
    }
  }

  function setupSourceFitmentDelegates(root) {
    if (root.dataset.parityFitmentSourceDelegatesReady === 'true') return;

    document.addEventListener('click', (event) => {
      const control = event.target.closest?.('.cm_vehicle-widget_button__clear, .cm_verify-fitment_change');
      if (!control || !getSourceFitmentWidgetFromNode(control)) return;

      if (control.matches('.cm_vehicle-widget_button__clear')) {
        beginFitmentProxyClear(root);
      }

      scheduleFitmentProxySync(root, 80);
    });

    ['input', 'change'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        if (!event.target.matches?.('select')) return;
        if (!getSourceFitmentWidgetFromNode(event.target)) return;
        scheduleFitmentProxySync(root, 120);
      });
    });

    root.dataset.parityFitmentSourceDelegatesReady = 'true';
  }

  function setupSourceFitmentObserver(root) {
    const widget = getSourceFitmentWidget();
    if (!widget) return;

    widget.querySelectorAll('select, .cm_vehicle-widget_button, .cm_verify-fitment_change').forEach((control) => {
      if (control.dataset.parityFitmentSourceControlReady === 'true') return;
      const isClearControl = control.matches('.cm_vehicle-widget_button__clear');

      ['input', 'change', 'click'].forEach((eventName) => {
        control.addEventListener(eventName, () => {
          if (eventName === 'click' && isClearControl) {
            beginFitmentProxyClear(root);
          }

          scheduleFitmentProxySync(root, 120);
        });
      });

      control.dataset.parityFitmentSourceControlReady = 'true';
    });

    if (widget.dataset.parityFitmentSourceObserverReady === 'true') return;

    const observer = new MutationObserver(() => {
      setupSourceFitmentObserver(root);
      scheduleFitmentProxySync(root, 80);
    });

    observer.observe(widget, {
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      childList: true,
      subtree: true,
    });

    widget.dataset.parityFitmentSourceObserverReady = 'true';
  }

  function setupFitment(root) {
    root.querySelectorAll('[data-parity-fitment-content]').forEach((fitment) => {
      if (fitment.dataset.parityFitmentReady === 'true') return;

      const source = fitment.querySelector('[data-parity-fitment-source]');
      const container = fitment.querySelector('[data-parity-fitment-groups]');
      if (source && container) {
        let text = '';
        try {
          text = JSON.parse(source.textContent || '""');
        } catch (_error) {
          text = source.textContent || '';
        }
        renderFitmentGroups(container, parseFitmentText(text));
      }

      fitment.dataset.parityFitmentReady = 'true';
    });

    setupFitmentSelectorProxy(root);
  }

  function isMobileDetailsView() {
    return window.matchMedia('(max-width: 768.98px)').matches;
  }

  function getReviewWidgetMount() {
    const widget = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
    if (!widget) return null;

    return widget.closest('.shopify-block.shopify-app-block') || widget;
  }

  function refreshOpenReviewHeight(root) {
    const content = root.querySelector('[data-parity-reviews-mobile-section][open] > .parity-pdp-details__item-content');
    if (!content || content.style.maxHeight === 'none') return;

    content.style.maxHeight = `${content.scrollHeight}px`;
  }

  function placeReviewWidget(root) {
    const mount = getReviewWidgetMount();
    if (!mount) return null;

    const slotName = isMobileDetailsView() ? 'mobile' : 'desktop';
    const slot = root.querySelector(`[data-parity-reviews-widget-slot="${slotName}"]`)
      || root.querySelector('[data-parity-reviews-widget-slot]');
    if (!slot) return mount;

    if (mount.parentElement !== slot) {
      slot.appendChild(mount);
    }

    mount.hidden = false;
    refreshOpenReviewHeight(root);
    return mount;
  }

  function openReviews(root, scrollToReviews = true) {
    placeReviewWidget(root);

    const mobileSection = root.querySelector('[data-parity-reviews-mobile-section]');
    const desktopSection = root.querySelector('[data-parity-reviews-desktop-section]');
    const target = isMobileDetailsView() ? mobileSection : desktopSection;
    if (!target) return;

    let openedFromClick = false;
    if (target.matches('details') && !target.open) {
      const summary = target.querySelector('.parity-pdp-details__summary');
      if (summary) {
        openedFromClick = true;
        summary.click();
      }
    }

    if (!scrollToReviews) return;

    window.setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, openedFromClick ? 320 : 0);
  }

  function setupReviewTriggers(root) {
    document.querySelectorAll('.jdgm-preview-badge, .product-info__rating, [data-parity-reviews-trigger]').forEach((trigger) => {
      if (trigger.dataset.parityReviewsTriggerReady === 'true') return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openReviews(root, true);
      }, true);

      trigger.dataset.parityReviewsTriggerReady = 'true';
    });

    document.querySelectorAll('[data-parity-write-review-trigger]').forEach((trigger) => {
      if (trigger.dataset.parityWriteReviewTriggerReady === 'true') return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openReviews(root, true);

        window.setTimeout(() => {
          const writeReviewLink = root.querySelector('.jdgm-write-rev-link')
            || document.querySelector('.jdgm-write-rev-link');

          if (writeReviewLink) {
            writeReviewLink.click();
          }
        }, 420);
      }, true);

      trigger.dataset.parityWriteReviewTriggerReady = 'true';
    });

    root.querySelectorAll('[data-parity-reviews-mobile-section] > .parity-pdp-details__summary').forEach((summary) => {
      if (summary.dataset.parityReviewsSummaryReady === 'true') return;

      summary.addEventListener('click', () => {
        window.setTimeout(() => placeReviewWidget(root), 0);
      });

      summary.dataset.parityReviewsSummaryReady = 'true';
    });
  }

  function scheduleReviewsRetry(root) {
    const attempts = Number(root.dataset.parityReviewsRetry || 0);
    if (attempts >= 20) return;

    root.dataset.parityReviewsRetry = String(attempts + 1);
    window.setTimeout(() => setupReviews(root), 500);
  }

  function setReviewsEmptyState(section, empty) {
    section.classList.toggle('parity-pdp-details__item--reviews-empty', empty);
    section.dataset.parityReviewsEmpty = empty ? 'true' : 'false';
  }

  function reviewStarFill(average, scaleMax = 5) {
    const rating = Number(average);
    const max = Number(scaleMax);
    if (!Number.isFinite(rating) || !Number.isFinite(max) || rating <= 0 || max <= 0) return '0%';

    return `${Math.max(0, Math.min(100, (rating / max) * 100))}%`;
  }

  function setupReviews(root) {
    const sections = Array.from(root.querySelectorAll('[data-parity-reviews-section]'));
    if (!sections.length) return;

    const badge = document.querySelector('.jdgm-prev-badge[data-number-of-reviews]');
    const mount = getReviewWidgetMount();
    if (!badge && !mount) {
      scheduleReviewsRetry(root);
      return;
    }

    const count = badge ? Number(badge.dataset.numberOfReviews || 0) : 0;
    const average = badge ? Number(badge.dataset.averageRating || 0) : 0;
    const hasReviews = count > 0 && average > 0;

    const heading = `${count} Customer ${count === 1 ? 'Review' : 'Reviews'}`;
    const averageLabel = hasReviews ? `${average.toFixed(1).replace(/\.0$/, '')}/5` : 'Be First To Rate It!';

    sections.forEach((section) => {
      section.hidden = false;
      setReviewsEmptyState(section, !hasReviews);
      section.querySelectorAll('[data-parity-reviews-heading]').forEach((node) => {
        node.textContent = heading;
      });
      section.querySelectorAll('[data-parity-reviews-average]').forEach((node) => {
        node.textContent = averageLabel;
      });
      section.querySelectorAll('[data-parity-reviews-stars]').forEach((node) => {
        node.textContent = hasReviews ? '★★★★★' : '';
        node.style.setProperty('--parity-pdp-review-star-fill', reviewStarFill(average));
        if (hasReviews) {
          node.setAttribute('aria-label', `${averageLabel} average rating`);
        } else {
          node.removeAttribute('aria-label');
        }
      });
    });

    if (!placeReviewWidget(root)) {
      scheduleReviewsRetry(root);
    }

    setupReviewTriggers(root);

    if (root.dataset.parityReviewsResizeReady !== 'true') {
      window.addEventListener('resize', () => placeReviewWidget(root), { passive: true });
      root.dataset.parityReviewsResizeReady = 'true';
    }
  }

  function setupDetails() {
    document.querySelectorAll(DETAILS_SELECTOR).forEach((root) => {
      setupTabs(root);
      setupReadMore(root);
      setupAccordions(root);
      setupPartNumbers(root);
      setupFitment(root);
      setupReviews(root);
    });
  }

  function updateAllShells() {
    document.querySelectorAll(SELECTOR).forEach((shell) => {
      moveExternalBelow(shell);
      setupDescriptionPreview(shell);
      setupRecommendations(shell);
    });
    setupDetails();
  }

  window.addEventListener('load', updateAllShells);
  document.addEventListener('shopify:section:load', updateAllShells);
  document.addEventListener('shopify:section:reorder', updateAllShells);
  updateAllShells();
})();
